import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import { 
  Building2, 
  Shield, 
  Server, 
  CheckCircle,
  XCircle,
  ArrowRight,
  Droplets,
  Wind,
  MapPin,
  Zap,
  Layers,
  Lock,
  Globe,
  AlertTriangle,
  TrendingUp,
  Calendar,
  Settings,
  Activity,
  Radio
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/LanguageContext';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/sections/ContactForm';
import { HreflangTags } from '@/components/HreflangTags';

const IoTForCitiesPage = () => {
  const { t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>IoT Infrastructure for Smart Cities | Carrier-Grade Networks for Municipalities | 0g Baltics</title>
        <meta name="description" content="Deploy carrier-grade IoT infrastructure for smart cities. Learn how municipalities can build sustainable, scalable IoT networks for metering, monitoring and urban services." />
        <meta name="keywords" content="smart city IoT, carrier-grade IoT infrastructure, municipal IoT network, smart city infrastructure, LoRaWAN smart city, urban IoT deployment" />
        <link rel="canonical" href="https://0gbaltics.com/iot-for-cities" />
        <meta property="og:title" content="IoT Infrastructure for Smart Cities | 0g Baltics" />
        <meta property="og:description" content="Carrier-grade IoT infrastructure for municipalities and urban services." />
        <meta property="og:type" content="website" />
      </Helmet>
      <HreflangTags />

      <Header />
      
      <main className="pt-20">
        {/* HERO SECTION */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="cities-hero">
          <div className="container-0g">
            <div className="max-w-4xl">
              <div className="inline-flex items-center space-x-2 mb-6">
                <span className="tech-badge bg-teal-500/20 border-teal-500/30 text-teal-400">Smart City Infrastructure</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                IoT Infrastructure for{' '}
                <span className="text-teal-400">Smart Cities</span>
              </h1>
              <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                Build sustainable, carrier-grade IoT networks for municipalities. Deploy smart metering, environmental monitoring, asset tracking and urban services on reliable infrastructure designed to last.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  className="btn-primary inline-flex items-center justify-center gap-2 text-base"
                  onClick={scrollToContact}
                  data-testid="cities-cta-primary"
                >
                  <Calendar className="h-5 w-5" />
                  Discuss Your Smart City Project
                </Button>
                <Link to="/tektelic">
                  <Button 
                    variant="outline"
                    className="border-2 border-white/30 text-white hover:bg-white/10 px-6 py-3 font-semibold inline-flex items-center justify-center gap-2 text-base"
                  >
                    Explore LoRaWAN Infrastructure
                    <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION — CARRIER GRADE IOT INFRASTRUCTURE */}
        <section className="py-20 md:py-32 bg-white" data-testid="carrier-grade-section">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
              <div>
                <p className="section-label">Infrastructure Standards</p>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  Carrier-Grade IoT Infrastructure for Cities
                </h2>
                
                <div className="prose prose-slate max-w-none">
                  <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                    Cities rely on infrastructure that must operate continuously for many years. IoT networks used in public infrastructure must meet the same reliability standards as telecom networks.
                  </p>
                  
                  <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                    This level of reliability is known as <strong className="text-slate-900">carrier-grade infrastructure</strong>.
                  </p>
                  
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    Carrier-grade systems are designed to operate with extremely high availability, strong security and the ability to scale to very large deployments. They are built to support thousands or even millions of connected devices while maintaining stable network performance.
                  </p>
                  
                  <p className="text-slate-600 leading-relaxed">
                    Unlike experimental or consumer IoT deployments, carrier-grade infrastructure is designed for long-term public service operations.
                  </p>
                </div>
              </div>
              
              <div>
                <div className="bg-slate-50 border border-slate-200 p-6 rounded-sm mb-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Critical City Services Supported</h3>
                  <div className="space-y-3">
                    {[
                      { icon: Droplets, text: 'Smart metering' },
                      { icon: Wind, text: 'Environmental monitoring' },
                      { icon: Activity, text: 'Infrastructure monitoring' },
                      { icon: MapPin, text: 'Public asset management' },
                      { icon: Zap, text: 'Energy management' },
                      { icon: Building2, text: 'Urban mobility services' },
                    ].map((service, index) => {
                      const Icon = service.icon;
                      return (
                        <div key={index} className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-sm bg-teal-100 flex items-center justify-center">
                            <Icon className="h-4 w-4 text-teal-600" />
                          </div>
                          <span className="text-slate-700">{service.text}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <div className="bg-teal-50 border border-teal-200 p-6 rounded-sm">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Carrier-Grade Characteristics</h3>
                  <div className="space-y-2">
                    {[
                      'Very high network availability',
                      'Industrial-grade hardware for outdoor environments',
                      'Advanced network monitoring and management',
                      'Secure communications and encrypted data transport',
                      'Scalability for large city-wide deployments',
                      'Long hardware lifecycle and vendor support',
                    ].map((item, index) => (
                      <div key={index} className="flex items-start space-x-2">
                        <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-12 bg-slate-900 p-8 rounded-sm">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-sm bg-teal-600 flex items-center justify-center flex-shrink-0">
                  <Radio className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-white text-lg leading-relaxed">
                    This is why <strong className="text-teal-400">0g Baltics deploys carrier-grade LoRaWAN gateways such as Tektelic infrastructure</strong> when designing IoT networks for cities and utilities.
                  </p>
                  <p className="text-slate-400 mt-2">
                    Carrier-grade systems ensure that smart city deployments remain reliable, secure and scalable for many years.
                  </p>
                  <Link to="/tektelic" className="inline-flex items-center text-teal-400 font-medium mt-4 hover:underline">
                    Learn about Tektelic infrastructure
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION — CONSUMER IOT VS CARRIER GRADE IOT */}
        <section className="py-20 md:py-32 bg-slate-50" data-testid="comparison-section">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <p className="section-label">Infrastructure Comparison</p>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                Consumer IoT vs Carrier-Grade IoT Infrastructure
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Consumer IoT deployments are typically designed for small pilot projects or consumer devices, while carrier-grade infrastructure is designed for large public infrastructure systems.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* Consumer IoT */}
              <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                <div className="bg-slate-200 p-6">
                  <h3 className="text-xl font-semibold text-slate-700">Consumer IoT</h3>
                  <p className="text-sm text-slate-500 mt-1">Experimental and small-scale</p>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {[
                      'Small deployments',
                      'Experimental projects',
                      'Limited reliability',
                      'Short hardware lifecycle',
                      'Limited network monitoring',
                    ].map((item, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <XCircle className="h-5 w-5 text-slate-400 flex-shrink-0" />
                        <span className="text-slate-600">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Carrier-Grade IoT */}
              <div className="bg-white border-2 border-teal-500 rounded-sm overflow-hidden relative">
                <div className="absolute top-4 right-4 bg-teal-600 text-white text-xs font-bold px-2 py-1 rounded">RECOMMENDED</div>
                <div className="bg-teal-600 p-6">
                  <h3 className="text-xl font-semibold text-white">Carrier-Grade IoT</h3>
                  <p className="text-sm text-teal-100 mt-1">Production infrastructure</p>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {[
                      'City-scale deployments',
                      'Production infrastructure',
                      'Telecom-level reliability',
                      'Industrial hardware lifecycle',
                      'Advanced network management',
                    ].map((item, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0" />
                        <span className="text-slate-900 font-medium">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="max-w-3xl mx-auto mt-12 text-center">
              <p className="text-slate-700 text-lg">
                Cities require <strong className="text-slate-900">carrier-grade infrastructure</strong> to avoid failures and ensure long-term sustainability of smart city projects.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION — WHY MOST SMART CITY PROJECTS FAIL */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="failure-section">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 rounded-sm bg-amber-500 flex items-center justify-center">
                    <AlertTriangle className="h-6 w-6 text-white" />
                  </div>
                  <p className="text-xs font-bold uppercase tracking-widest text-amber-400">Common Pitfalls</p>
                </div>
                <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                  Why Most Smart City Projects Fail
                </h2>
                <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                  Many smart city projects start with promising pilot programs but fail to scale across the entire city. This often happens because the underlying infrastructure was not designed for long-term deployment.
                </p>
                
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white">Common Reasons for Failure</h3>
                  {[
                    'Selecting technologies that do not scale',
                    'Using consumer-grade hardware instead of industrial infrastructure',
                    'Lack of long-term network planning',
                    'Disconnected systems that cannot share infrastructure',
                    'Poor cost modelling and unclear return on investment',
                  ].map((reason, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <XCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-300">{reason}</span>
                    </div>
                  ))}
                </div>
                
                <p className="text-slate-400 mt-8">
                  As a result, cities end up with fragmented pilot projects that never evolve into sustainable infrastructure.
                </p>
              </div>
              
              <div className="bg-slate-800 border border-slate-700 p-8 rounded-sm">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 rounded-sm bg-teal-600 flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">The Right Approach</h3>
                </div>
                
                <p className="text-slate-300 mb-6 leading-relaxed">
                  Successful smart city deployments follow a different approach.
                </p>
                
                <p className="text-white text-lg font-medium mb-6">
                  They start by building a <span className="text-teal-400">shared IoT infrastructure capable of supporting multiple applications at once.</span>
                </p>
                
                <p className="text-slate-400 leading-relaxed">
                  This allows cities to gradually deploy new services on the same network without rebuilding infrastructure every time.
                </p>
                
                <div className="mt-8 pt-6 border-t border-slate-700">
                  <Link to="/connectivity" className="inline-flex items-center text-teal-400 font-medium hover:underline">
                    Learn about multi-network infrastructure
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION — BUILDING SUSTAINABLE SMART CITY INFRASTRUCTURE */}
        <section className="py-20 md:py-32 bg-white" data-testid="sustainable-section">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <p className="section-label">Strategic Approach</p>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                Building Sustainable Smart City Infrastructure
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                A successful smart city strategy focuses on infrastructure first. Instead of deploying isolated technology projects, cities build a common IoT network capable of supporting multiple urban services.
              </p>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
              {/* Architecture */}
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-6">Typical Architecture</h3>
                <div className="space-y-4">
                  {[
                    { icon: Globe, title: 'City-wide connectivity infrastructure', desc: 'LoRaWAN, Sigfox, NB-IoT coverage' },
                    { icon: Server, title: 'Carrier-grade gateways', desc: 'Industrial hardware for long-term operation' },
                    { icon: Lock, title: 'Secure cloud platforms', desc: 'Dashboards and data management' },
                    { icon: Layers, title: 'Scalable sensor ecosystems', desc: 'Multiple device types and vendors' },
                  ].map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <div key={index} className="flex items-start space-x-4 p-4 bg-slate-50 border border-slate-200 rounded-sm">
                        <div className="w-10 h-10 rounded-sm bg-teal-600 flex items-center justify-center flex-shrink-0">
                          <Icon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900">{item.title}</h4>
                          <p className="text-sm text-slate-600">{item.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Services */}
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-6">Progressive Service Deployment</h3>
                <p className="text-slate-600 mb-6">
                  Once the infrastructure is deployed, cities can progressively add new services:
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: Droplets, text: 'Smart metering' },
                    { icon: Wind, text: 'Environmental monitoring' },
                    { icon: MapPin, text: 'Smart parking' },
                    { icon: Activity, text: 'Public asset tracking' },
                    { icon: Zap, text: 'Energy monitoring' },
                    { icon: Building2, text: 'Building management' },
                  ].map((service, index) => {
                    const Icon = service.icon;
                    return (
                      <div key={index} className="flex items-center space-x-3 p-3 bg-teal-50 border border-teal-100 rounded-sm">
                        <Icon className="h-5 w-5 text-teal-600 flex-shrink-0" />
                        <span className="text-slate-700 text-sm font-medium">{service.text}</span>
                      </div>
                    );
                  })}
                </div>
                
                <div className="mt-8 p-4 bg-slate-900 rounded-sm">
                  <p className="text-slate-300 text-sm">
                    This approach allows cities to <span className="text-teal-400 font-medium">scale smart city services while controlling costs</span> and ensuring long-term sustainability.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION — CTA */}
        <section className="py-20 md:py-32 cta-gradient" data-testid="cities-cta">
          <div className="container-0g">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                Design Your City IoT Infrastructure
              </h2>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                0g Baltics supports municipalities and infrastructure operators in designing and deploying carrier-grade IoT networks across the Baltics and Europe. Our team helps cities evaluate infrastructure options, plan pilot deployments and scale successful smart city services.
              </p>
              <Button 
                className="btn-primary bg-teal-500 hover:bg-teal-400 text-white px-8 py-4 text-lg inline-flex items-center gap-2"
                onClick={scrollToContact}
                data-testid="cities-final-cta"
              >
                <Calendar className="h-5 w-5" />
                Discuss Your Smart City Project
              </Button>
            </div>
          </div>
        </section>

        {/* CONTACT FORM */}
        <ContactForm />
      </main>

      <Footer />
    </>
  );
};

export default IoTForCitiesPage;
